'use client';

import * as React from 'react';
import { ArrowLeft, Landmark, MapPin, Navigation, Sparkles, Store, UtensilsCrossed, Info, Search, LocateFixed, Loader2, Handshake, Clock, Crown, Map as MapIcon } from 'lucide-react';
import Image from 'next/image';

import type { PointOfInterest, POIType } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import PrayerTimesCard from './prayer-times-card';

interface LocationSidebarProps {
  pois: PointOfInterest[];
  selectedPoi: PointOfInterest | null;
  onSelectPoi: (poi: PointOfInterest | null) => void;
  onShowOnMap: (poi: PointOfInterest | null) => void;
  onClearSelection: () => void;
  activeFilter: POIType | 'all';
  onSetFilter: (filter: POIType | 'all') => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  prayerTimesCoords: { lat: number; lng: number };
  onFindNearby: () => void;
  isFindingNearby: boolean;
}

const poiTypeIcons: Record<POIType, React.ElementType> = {
  restaurant: UtensilsCrossed,
  prayer_area: Landmark,
  business: Store,
};

const poiTypeLabels: Record<POIType, string> = {
  restaurant: 'Food',
  prayer_area: 'Prayer',
  business: 'Business',
};

const partnerLogos: { [key: string]: string } = {
  'Big Buns – Browns Plains': '/icons/big-buns.jpg',
  'Big Buns – Upper Mount Gravatt': '/icons/big-buns.jpg',
  'Baba Kumpir': '/icons/baba-kumpir.jpg',
  'Smashhh Burgers (Underwood)': '/icons/Smash-Burger.jpg'
};

const partnerBanners: { [key: string]: string } = {
  'Big Buns – Browns Plains': '/icons/BigBunsBanner.jpeg',
  'Big Buns – Upper Mount Gravatt': '/icons/BigBunsBanner.jpeg',
  'Baba Kumpir': '/icons/BabaKumpirBanner.jpg',
  'Smashhh Burgers (Underwood)': '/icons/SmashBanner.jpg',
};


const LocationDetail = ({ poi, onBack, onShowOnMap }: { poi: PointOfInterest; onBack: () => void; onShowOnMap: (poi: PointOfInterest) => void; }) => {
  const Icon = poiTypeIcons[poi.type];
  const genericSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(poi.name + ', ' + poi.address)}`;
  const isFoundingPartner = poi.isPartner && (poi.name.includes('Big Buns') || poi.name.includes('Baba Kumpir') || poi.name.includes('Smashhh Burgers'));
  
  const logoSrc = partnerLogos[poi.name];
  const bannerSrc = partnerBanners[poi.name];


  return (
    <div className="flex flex-col h-full">
      <header className="p-4 border-b">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Button>
        {bannerSrc && (
          <div className="mt-4 -mx-4 h-32 overflow-hidden relative">
              <Image src={bannerSrc} alt={`${poi.name} banner`} layout="fill" className="w-full h-full object-cover" data-ai-hint="restaurant interior" />
          </div>
        )}
        <div className="flex items-center gap-3 mt-4">
            {logoSrc && (
                <Image src={logoSrc} alt={`${poi.name} logo`} width={48} height={48} className="rounded-md w-12 h-12 object-cover" />
            )}
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">{poi.name}</h2>
        </div>
        <div className="flex items-center gap-4 mt-2 text-muted-foreground">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            <span>{poiTypeLabels[poi.type]}</span>
          </div>
          {poi.isPartner && (
             <Badge variant="secondary" className="border-yellow-500/50 bg-yellow-500/10 text-yellow-600">
                {isFoundingPartner ? <Crown className="w-3 h-3 mr-1.5" /> : <Handshake className="w-3 h-3 mr-1.5" />}
                {isFoundingPartner ? 'Founding Partner' : 'Partnered'}
            </Badge>
          )}
        </div>
      </header>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div className="space-y-2">
              <Button className="w-full" onClick={() => onShowOnMap(poi)}>
                <MapIcon className="mr-2 h-4 w-4" />
                View on Map
              </Button>
              <Button className="w-full" variant="secondary" asChild>
                <a href={genericSearchUrl} target="_blank" rel="noopener noreferrer">
                    <Navigation className="mr-2 h-4 w-4" />
                    Get Directions
                </a>
              </Button>
          </div>
          <Separator />
          <div className="space-y-4">
            <div className="flex items-start gap-3 text-sm">
              <MapPin className="w-5 h-5 mt-0.5 text-muted-foreground" />
              <span>{poi.address}</span>
            </div>
             {poi.hours && (
              <div className="flex items-start gap-3 text-sm">
                <Clock className="w-5 h-5 mt-0.5 text-muted-foreground" />
                <span>{poi.hours}</span>
              </div>
            )}
          </div>
          <Separator />
          <div className="space-y-3">
            {poi.halal_certification && <InfoItem label="Halal Certification" value={poi.halal_certification} />}
            {poi.prayer_space_size && <InfoItem label="Prayer Space" value={poi.prayer_space_size} />}
          </div>
          {poi.augmentedInformation && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2 text-foreground">
                  <Sparkles className="w-5 h-5 text-primary" />
                  AI-Augmented Info
                </h3>
                <p className="text-sm text-muted-foreground mt-2">{poi.augmentedInformation}</p>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
  <div className="text-sm">
    <p className="font-medium text-muted-foreground">{label}</p>
    <p>{value}</p>
  </div>
);

const LocationCard = ({ poi, onSelectPoi, onShowOnMap }: { poi: PointOfInterest; onSelectPoi: (poi: PointOfInterest) => void; onShowOnMap: (poi: PointOfInterest) => void; }) => {
  const Icon = poiTypeIcons[poi.type];
  const isFoundingPartner = poi.isPartner && (poi.name.includes('Big Buns') || poi.name.includes('Baba Kumpir') || poi.name.includes('Smashhh Burgers'));

  const logoSrc = partnerLogos[poi.name];

  return (
    <Card
      className="hover:shadow-md transition-shadow bg-background"
    >
      <div className="p-4 flex items-center gap-4">
        {logoSrc && (
          <Image src={logoSrc} alt={`${poi.name} logo`} width={40} height={40} className="rounded-md w-10 h-10 object-cover" />
        )}
        <div className="flex-1 min-w-0" onClick={() => onSelectPoi(poi)} role="button">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-bold text-base md:text-lg text-foreground leading-tight truncate">{poi.name}</h3>
             <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {poi.isPartner && (
                <Badge variant="secondary" className="border-yellow-500/50 bg-yellow-500/10 text-yellow-600 text-xs">
                  {isFoundingPartner ? <Crown className="w-3 h-3 mr-1" /> : <Handshake className="w-3 h-3 mr-1" />}
                  {isFoundingPartner ? 'Founding Partner' : 'Partner'}
                </Badge>
              )}
               <Badge 
                variant={poi.type === 'restaurant' ? 'default' : 'secondary'} 
                className={cn('flex-shrink-0', 
                  poi.type === 'restaurant' && 'bg-primary text-primary-foreground',
                  poi.type === 'prayer_area' && 'bg-blue-500 text-white',
                  poi.type === 'business' && 'bg-amber-500 text-black'
                )}
              >
                <Icon className="w-3 h-3 mr-1.5" />
                {poiTypeLabels[poi.type]}
              </Badge>
            </div>
          </div>
          <p className="text-sm text-muted-foreground truncate">{poi.address}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onShowOnMap(poi)}>
            <MapIcon className="w-5 h-5 text-primary" />
        </Button>
      </div>
    </Card>
  );
};

const LocationList = ({ pois, onSelectPoi, onShowOnMap, activeFilter, onSetFilter, searchQuery, onSearchQueryChange, prayerTimesCoords, onFindNearby, isFindingNearby }: Omit<LocationSidebarProps, 'selectedPoi' | 'onClearSelection'>) => {
  
  const handlePoiSelection = (poi: PointOfInterest) => {
    onSelectPoi(poi);
  }

  const partneredPois = pois.filter(p => p.isPartner);
  const regularPois = pois.filter(p => !p.isPartner);
  
  return (
    <div className="flex flex-col h-full bg-background">
      <header className="p-4 sticky top-0 bg-background z-10 space-y-4 border-b">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl font-bold text-foreground">Discover</h1>
          <Button variant="outline" size="sm" onClick={onFindNearby} disabled={isFindingNearby}>
            {isFindingNearby ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LocateFixed className="mr-2 h-4 w-4" />}
            Find Nearby
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input 
            placeholder="Search by name or address..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
          />
        </div>
        <div className="space-y-2">
            <PrayerTimesCard lat={prayerTimesCoords.lat} lng={prayerTimesCoords.lng} />
        </div>
        <div>
          <FilterButtons activeFilter={activeFilter} onSetFilter={onSetFilter} />
        </div>
      </header>
      <div className="flex-1 flex flex-col">
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {partneredPois.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Handshake className="w-5 h-5 text-yellow-600" />
                  <h2 className="text-lg font-semibold text-foreground">Partnered Locations</h2>
                </div>
                <div className="space-y-4">
                   {partneredPois.map((poi) => (
                    <LocationCard key={poi.id} poi={poi} onSelectPoi={handlePoiSelection} onShowOnMap={onShowOnMap} />
                  ))}
                </div>
                <Separator />
              </div>
            )}
            
            {regularPois.length > 0 ? (
              regularPois.map((poi) => (
                <LocationCard key={poi.id} poi={poi} onSelectPoi={handlePoiSelection} onShowOnMap={onShowOnMap} />
              ))
            ) : (
               !searchQuery && partneredPois.length > 0 ? null : ( // Don't show "No locations" if partners are showing
                <div className="text-center py-10">
                  <p className="text-muted-foreground">No locations found. Try a different search or filter.</p>
                </div>
               )
            )}
          </div>
        </ScrollArea>
        <footer className="p-4 border-t bg-background">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="w-4 h-4" />
            <div>
              <p>The app is still in development, so there might be some bugs and errors.</p>
              <p>Please report if you find any.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const FilterButtons = ({ activeFilter, onSetFilter }: { activeFilter: POIType | 'all'; onSetFilter: (filter: POIType | 'all') => void; }) => {
  const filters: (POIType | 'all')[] = ['all', 'restaurant', 'prayer_area', 'business'];
  const filterLabels: Record<POIType | 'all', string> = {
    all: 'All',
    ...poiTypeLabels,
  };

  const filterIcons: Record<POIType | 'all', React.ElementType | null> = {
    all: null,
    restaurant: UtensilsCrossed,
    prayer_area: Landmark,
    business: Store
  }

  return (
    <div className="flex gap-2">
      {filters.map((filter) => {
        const Icon = filterIcons[filter]
        return (
        <Button
          key={filter}
          variant={activeFilter === filter ? 'default' : 'secondary'}
          onClick={() => onSetFilter(filter)}
          className={cn(
            'transition-all rounded-full flex-1 text-xs md:text-sm',
             activeFilter === filter ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
          )}
        >
          {Icon && <Icon className="mr-2 h-4 w-4" />}
          {filterLabels[filter]}
        </Button>
      )}
      )}
    </div>
  );
}

export default function LocationSidebar(props: LocationSidebarProps) {
  if (props.selectedPoi) {
    return <LocationDetail poi={props.selectedPoi} onBack={props.onClearSelection} onShowOnMap={props.onShowOnMap} />;
  }
  return <LocationList {...props} />;
}
